/**
 * Hook personnalisé pour les effets sonores du jeu
 * Inspiré de Duolingo
 */
import { useCallback, useEffect, useRef } from 'react';

// Créer un contexte audio global
const audioContext = typeof window !== 'undefined' ? new (window.AudioContext || window.webkitAudioContext)() : null;

// Fonction pour générer des sons avec Web Audio API
const createSound = (type) => {
  if (!audioContext) return () => {};
  
  return () => {
    try {
      // Reprendre le contexte audio si suspendu (requis par certains navigateurs)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }

      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      const now = audioContext.currentTime;
      
      switch (type) {
        case 'correct':
          // Son de succès - deux notes ascendantes joyeuses
          oscillator.frequency.setValueAtTime(523.25, now); // C5
          oscillator.frequency.setValueAtTime(659.25, now + 0.1); // E5
          oscillator.frequency.setValueAtTime(783.99, now + 0.2); // G5
          gainNode.gain.setValueAtTime(0.3, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
          oscillator.start(now);
          oscillator.stop(now + 0.4);
          break;
          
        case 'incorrect':
          // Son d'erreur - note descendante douce
          oscillator.frequency.setValueAtTime(311.13, now); // Eb4
          oscillator.frequency.exponentialRampToValueAtTime(220, now + 0.2); // A3
          gainNode.gain.setValueAtTime(0.2, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
          oscillator.start(now);
          oscillator.stop(now + 0.3);
          break;
          
        case 'combo':
          // Son de combo - arpège ascendant rapide
          const comboOsc1 = audioContext.createOscillator();
          const comboOsc2 = audioContext.createOscillator();
          const comboOsc3 = audioContext.createOscillator();
          const comboGain = audioContext.createGain();
          
          comboOsc1.connect(comboGain);
          comboOsc2.connect(comboGain);
          comboOsc3.connect(comboGain);
          comboGain.connect(audioContext.destination);
          
          comboOsc1.frequency.setValueAtTime(523.25, now); // C5
          comboOsc2.frequency.setValueAtTime(659.25, now + 0.08); // E5
          comboOsc3.frequency.setValueAtTime(783.99, now + 0.16); // G5
          
          comboGain.gain.setValueAtTime(0.25, now);
          comboGain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);
          
          comboOsc1.start(now);
          comboOsc1.stop(now + 0.15);
          comboOsc2.start(now + 0.08);
          comboOsc2.stop(now + 0.23);
          comboOsc3.start(now + 0.16);
          comboOsc3.stop(now + 0.5);
          break;
          
        case 'levelUp':
          // Son de victoire - fanfare
          const fanfareFreqs = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
          fanfareFreqs.forEach((freq, i) => {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            osc.connect(gain);
            gain.connect(audioContext.destination);
            
            osc.frequency.setValueAtTime(freq, now + i * 0.12);
            gain.gain.setValueAtTime(0.2, now + i * 0.12);
            gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.3);
            
            osc.start(now + i * 0.12);
            osc.stop(now + i * 0.12 + 0.3);
          });
          break;
          
        case 'click':
          // Son de clic léger
          oscillator.frequency.setValueAtTime(800, now);
          gainNode.gain.setValueAtTime(0.1, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
          oscillator.start(now);
          oscillator.stop(now + 0.05);
          break;
          
        case 'heartLost':
          // Son de perte de cœur - note triste descendante
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(440, now); // A4
          oscillator.frequency.exponentialRampToValueAtTime(220, now + 0.3); // A3
          gainNode.gain.setValueAtTime(0.2, now);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
          oscillator.start(now);
          oscillator.stop(now + 0.4);
          break;
          
        default:
          break;
      }
    } catch (e) {
      console.log('Audio error:', e);
    }
  };
};

export const useGameSounds = () => {
  const soundsEnabled = useRef(true);
  
  // Vérifier les préférences utilisateur
  useEffect(() => {
    const saved = localStorage.getItem('spirify_sounds_enabled');
    if (saved !== null) {
      soundsEnabled.current = saved === 'true';
    }
  }, []);
  
  const playSound = useCallback((type) => {
    if (!soundsEnabled.current) return;
    const sound = createSound(type);
    sound();
  }, []);
  
  const playCorrect = useCallback(() => playSound('correct'), [playSound]);
  const playIncorrect = useCallback(() => playSound('incorrect'), [playSound]);
  const playCombo = useCallback(() => playSound('combo'), [playSound]);
  const playLevelUp = useCallback(() => playSound('levelUp'), [playSound]);
  const playClick = useCallback(() => playSound('click'), [playSound]);
  const playHeartLost = useCallback(() => playSound('heartLost'), [playSound]);
  
  const toggleSounds = useCallback((enabled) => {
    soundsEnabled.current = enabled;
    localStorage.setItem('spirify_sounds_enabled', enabled.toString());
  }, []);
  
  const isSoundEnabled = useCallback(() => soundsEnabled.current, []);
  
  return {
    playCorrect,
    playIncorrect,
    playCombo,
    playLevelUp,
    playClick,
    playHeartLost,
    toggleSounds,
    isSoundEnabled
  };
};

export default useGameSounds;
